import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import styles from './Dialog.module.css';

interface DialogProps {
  title: string;
  children: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
  open?: boolean;
  dismissible?: boolean;
  size?: 'default' | 'wide';
}

export function Dialog({ title, children, onClose, actions, open, dismissible = true, size = 'default' }: DialogProps) {
  const titleId = useId();
  const [dialogRef, setDialogRef] = useState<HTMLDialogElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const close = () => {
    if (dialogRef?.open) dialogRef.close();
    else onCloseRef.current();
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (dismissible && e.target === dialogRef) {
      close();
    }
  };

  const handleCancel = (event: React.SyntheticEvent<HTMLDialogElement>) => {
    if (!dismissible) event.preventDefault();
  };

  useEffect(() => {
    if (dialogRef) {
      dialogRef.showModal();
      const handleBackdropClose = () => {
        onCloseRef.current();
      };
      dialogRef.addEventListener('close', handleBackdropClose);
      return () => dialogRef.removeEventListener('close', handleBackdropClose);
    }
  }, [dialogRef, open]);

  return (
    <div>
      <dialog ref={setDialogRef} className={styles['modal-content']} data-size={size} onClick={handleDialogClick} onCancel={handleCancel} aria-labelledby={titleId}>
        <div className={styles['modal-header']}>
          <h5 id={titleId} className={styles['modal-title']}>{title}</h5>
          {dismissible && <button type="button" className={styles['btn-close']} onClick={close} aria-label="Close">&times;</button>}
        </div>
        <div className={styles['modal-body']}>
          {children}
        </div>
        {actions && (
          <div className={styles['modal-footer']}>
            {actions}
          </div>
        )}
      </dialog>
    </div>
  );
}
